import React from "react";

// === helpers: luminancja i wykrycie tonu powierzchni ===
function parseRGB(color: string) {
  // wspiera "rgb(a)" oraz "rgba"
  const m = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!m) return { r: 0, g: 0, b: 0 };
  const [, r, g, b] = m;
  return { r: +r, g: +g, b: +b };
}

function relativeLuminance({ r, g, b }: { r: number; g: number; b: number }) {
  const srgb = [r, g, b].map(v => v / 255).map(v => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));
  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
}

function getSurfaceFromEl(el: Element | null): "light" | "dark" | null {
  if (!el) return null;
  const labeled = (el as HTMLElement).closest<HTMLElement>('[data-surface],[data-surface-light],[data-surface-dark]');
  if (!labeled) return null;
  if (labeled.hasAttribute('data-surface-light')) return "light";
  if (labeled.hasAttribute('data-surface-dark')) return "dark";
  const v = labeled.getAttribute('data-surface');
  return v === "light" ? "light" : v === "dark" ? "dark" : null;
}

function pickUnderlyingElement(x: number, y: number, skip: Element | null) {
  const stack = document.elementsFromPoint(x, y);
  for (const el of stack) {
    if (!skip || (el !== skip && !skip.contains(el))) return el;
  }
  return stack[stack.length - 1] ?? null;
}

function getSurfaceToneAt(x: number, y: number, skipEl?: Element | null): 'light' | 'dark' {
  const el = pickUnderlyingElement(x, y, skipEl);
  if (!el) return 'dark';

  const tagged = getSurfaceFromEl(el);
  if (tagged) return tagged;

  // fallback: idź w górę DOM i licz luminancję first non-transparent bg
  let node: HTMLElement | null = el as HTMLElement;
  while (node && node !== document.documentElement) {
    const bg = getComputedStyle(node).backgroundColor;
    if (bg && !bg.includes("0)")) {
      const lum = relativeLuminance(parseRGB(bg));
      return lum > 0.5 ? "light" : "dark";
    }
    node = node.parentElement;
  }
  return "dark";
}

// Hook: nasłuchuje scroll/resize i wykrywa ton pod środkiem przycisku
import { useTheme } from "@/components/theme-provider";

function useSurfaceToneForFixedButton(btnRef: React.RefObject<HTMLButtonElement>) {
  const [tone, setTone] = React.useState<"light" | "dark">("dark");
  const { theme } = useTheme();

  React.useEffect(() => {
    let raf = 0;
    const update = () => {
      const btn = btnRef.current;
      if (!btn) return;

      // tymczasowo omijamy przycisk przy hit-test
      const prev = btn.style.pointerEvents;
      btn.style.pointerEvents = 'none';
      const rect = btn.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const t = getSurfaceToneAt(cx, cy, btn);
      btn.style.pointerEvents = prev;

      setTone(t);
    };

    const onScrollOrResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [btnRef, theme]);

  return tone;
}

export { useSurfaceToneForFixedButton };
